
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, BarChart, Bar } from 'recharts'

interface SiteAnalyticsChartsProps {
  dailyVisits: Array<{
    date: string
    visits: number
    unique_visitors: number
  }>
  trafficSources: Array<{
    source_name: string
    source_type: string
    visit_count: number
    unique_visitors: number
  }>
  topPages: Array<{
    page_path: string
    page_title: string | null
    page_views: number
    unique_visitors: number
    avg_time_on_page: number
  }>
  browserBreakdown: Array<{
    browser_type: string
    count: number
    percentage: number
  }>
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D']

export const SiteAnalyticsCharts = ({
  dailyVisits,
  trafficSources,
  topPages,
  browserBreakdown
}: SiteAnalyticsChartsProps) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Daily visits trend */}
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle>Visits Over Time</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dailyVisits}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Line 
                type="monotone" 
                dataKey="visits" 
                stroke="#8884d8" 
                strokeWidth={2}
                name="Total Visits"
              />
              <Line 
                type="monotone" 
                dataKey="unique_visitors" 
                stroke="#82ca9d" 
                strokeWidth={2}
                name="Unique Visitors"
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Traffic sources */}
      <Card>
        <CardHeader>
          <CardTitle>Traffic Sources</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={trafficSources}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ source_name, percentage }) => `${source_name} (${(percentage || 0).toFixed(1)}%)`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="visit_count"
              >
                {trafficSources.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Browser breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Browser Usage</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={browserBreakdown} layout="horizontal">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="browser_type" type="category" width={80} />
              <Tooltip />
              <Bar dataKey="count" fill="#8884d8" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Top pages */}
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle>Top Pages</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {topPages.slice(0, 10).map((page, index) => (
              <div key={page.page_path} className="flex items-center justify-between border-b pb-2">
                <div className="flex-1">
                  <div className="font-medium truncate">
                    {page.page_title || page.page_path}
                  </div>
                  <div className="text-sm text-muted-foreground truncate">
                    {page.page_path}
                  </div>
                </div>
                <div className="flex gap-4 text-sm">
                  <div className="text-center">
                    <div className="font-semibold">{page.page_views}</div>
                    <div className="text-muted-foreground">Views</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold">{page.unique_visitors}</div>
                    <div className="text-muted-foreground">Visitors</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold">
                      {Math.round(page.avg_time_on_page / 60)}m
                    </div>
                    <div className="text-muted-foreground">Avg Time</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
